import {
  BaseDateQuestion,
  BaseFormQuestion,
  BaseMultipleSelect,
  BaseRankingQuestion,
  BaseSingleSelect,
  isCalendar,
  isRecording,
  isCalendly,
  isScheduling,
  isForm,
  isMultipleSelect,
  isRankingQuestion,
  isSingleSelect,
} from '@hubertai/hubert-component-library';
import type {
  TAnyClientQuestionType,
  TCalendlyQuestion,
  TSchedulingQuestion,
  TRecordingQuestion,
  TAvailableLanguageCodes,
  TCalendarQuestion,
  TFormQuestion,
} from '@hubertai/screening-question-types';
import type { ComponentProps } from 'react';
import { BookingPreview } from './BookingPreview';
import { RecordingPreview } from './RecordingPreview';

interface PreviewQuestionProps extends ComponentProps<'div'> {
  questionConfig: TAnyClientQuestionType;
  language: TAvailableLanguageCodes;
}

const PreviewQuestion = ({ questionConfig, language, ...props }: PreviewQuestionProps) => {
  if (isSingleSelect(questionConfig)) {
    return (
      <div {...props}>
        <BaseSingleSelect rawQuestion={questionConfig} isPreview={true} language={language} onSend={() => {}} />
      </div>
    );
  }

  if (isMultipleSelect(questionConfig)) {
    return (
      <div {...props}>
        <BaseMultipleSelect rawQuestion={questionConfig} isPreview={true} language={language} onSend={() => {}} />
      </div>
    );
  }

  if (isRankingQuestion(questionConfig)) {
    return (
      <div {...props}>
        <BaseRankingQuestion
          mode="click"
          rawQuestion={questionConfig}
          language={language}
          onChange={() => {}}
          isPreview={false}
        />
      </div>
    );
  }

  if (isForm(questionConfig)) {
    // Element types are migrated into the library in waves; the ones that are not there yet
    // show as labelled placeholders, so the recruiter still sees the shape of the form.
    return (
      <div {...props}>
        <BaseFormQuestion
          rawQuestion={questionConfig as unknown as TFormQuestion<string>}
          language={language}
          isPreview={true}
        />
      </div>
    );
  }

  if (isCalendar(questionConfig)) {
    return (
      <div {...props}>
        <BaseDateQuestion
          rawQuestion={questionConfig as unknown as TCalendarQuestion<string>}
          language={language}
          isPreview={true}
        />
      </div>
    );
  }

  if (isRecording(questionConfig)) {
    // A capability card, not a recorder — see RecordingPreview for why.
    return (
      <div {...props}>
        <RecordingPreview question={questionConfig as unknown as TRecordingQuestion<string>} />
      </div>
    );
  }

  if (isScheduling(questionConfig) || isCalendly(questionConfig)) {
    // A capability card, not a live booking widget — see BookingPreview for why.
    return (
      <div {...props}>
        <BookingPreview
          question={questionConfig as unknown as TSchedulingQuestion<string> | TCalendlyQuestion<string>}
          language={language}
        />
      </div>
    );
  }

  return null;
};

export default PreviewQuestion;
